import React from 'react';
import { get, noop, forEach, cloneDeep, head, find } from 'lodash';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { authFlowCallerIdentificationMember as AuthFlowCallerIdentificationMemberView } from 'ui/views/widgets/authFlowCallerIdentificationMember';
import { AuthFlowCallerIdentificationMemberService } from 'services/authFlowCallerIdentificationMember';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { isInbound, getCallerTypeFromName } from 'resolvers/callStateEntities';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowCallerIdentificationMemberWidget } from 'ui/containers/AuthFlowCallerIdentificationMemberWidget';
 *
 * // render
 * <AuthFlowCallerIdentificationMemberWidget propName='propValue'/>
 */
class _AuthFlowCallerIdentificationMemberWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );
        this.startOutboundCall = this.startOutboundCall.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            requestPayload: {}
        };

        // create service instance
        this.authFlowCallerIdentificationMemberService = new AuthFlowCallerIdentificationMemberService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowCallerIdentificationMemberWidget.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationMemberService.cancel();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationMemberService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // get callType from store
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        // set requestPayload for table widget
        const requestPayload = {
            context: {
                module: module
            }
        };

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                requestPayload: requestPayload
            } );
        }
    }

    /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {} } = {} ) {
        log.debug( 'AuthFlowCallerIdentificationMemberWidget.handleActionClick()' );

        // get member id
        const memberId = get( item, 'id', null );
        const patientId = get( item, 'values.patientId', null );
        const firstName = get( item, 'values.firstName', '' );
        const lastName = get( item, 'values.lastName', '' );

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.widget.value.callerType );

        /*---------*/

        // when `initiateCall` group action is clicked, initiate outbound call
        if( 'groupAction' === type && 'initiateCall' === value.name ) {
            return this.initiateOutboundCall( {
                actionName: value.name,
                url: value.url,
                payload: {
                    id: memberId,
                    patientId: patientId,
                    firstName,
                    lastName,
                },
                callerType: callerType
            } );
        }

        /*---------*/

        // when `memberId` column action is clicked, register an inbound caller
        if( 'column' === type && 'memberId' === value.name ) {
            return this.registerInboundCaller( {
                actionName: value.name,
                url: value.actionConfig.url,
                payload: {
                    id: memberId,
                    patientId: patientId,
                    firstName,
                    lastName,
                },
                callerType: callerType
            } );
        }
    }

    /**
     * @desc Show an overlay with phone numbers to MSR.
     */
    async initiateOutboundCall( { actionName, url, payload, callerType } ) {
        log.debug( 'AuthFlowCallerIdentificationMemberWidget.initiateOutboundCall()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationMemberService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationMemberService.initiateOutboundCall( {
            url: url,
            data: payload,
            callerType: callerType
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {
                const _data = cloneDeep( data );

                // iterate over phone number options
                forEach( get( _data, 'data.options.phoneNumberOptions', [] ), ( option ) => {

                    let suffix;

                    // set suffix and default value based on option type
                    if ( option.isDefault ) {
                        suffix = getLabel( get( head( get( _data, 'fields', [] ) ), 'config.isDefaultTitle', {} ) );
                        _data.values = { 'phoneNumber': option.value };
                    } else {
                        suffix = getLabel( get( find( get( _data, 'data.options.contactType', [] ), { value: option.type }, {} ), 'title', {} ) );
                    }

                    // modify option title with suffix
                    option.title.value = `${option.title.value} (${suffix})`;
                } );

                overlay.open( {
                    settings: {
                        modalType: OVERLAY_VIEWS.SET_PHONE_NUMBER,
                        showCloseButton: false,
                        ignoreOutsideClick: false,
                    },
                    config: {},
                    data: {
                        ..._data,
                        payload,
                        callerType,
                        actionName,
                        onFormSubmit: this.startOutboundCall,
                        onCancelClick: () => {
                            overlay.close();
                        },
                    }
                } );
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * @desc Request backend to start an outbound call for member.
     */
    async startOutboundCall( { actions, actionName, payload, callerType, formData } ) {
        log.debug( 'AuthFlowCallerIdentificationMemberWidget.startOutboundCall()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationMemberService.cancel();

        // close overlay
        overlay.close();

        // show page level preloader
        pageLevelPreloader.show();

        // find Start Call action
        const startCallAction = find( actions, {name: 'submit'}, {} );

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationMemberService.startOutboundCall( {
            url: startCallAction.url,
            callerType: callerType,
            data: {
                data: {
                    ...payload,
                    ...formData
                }
            }
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {
                this.props.initCallState( data.callData ); // save call data in the store
                this.props.onNextActionClick( { name: actionName, memberId: payload.id, patientId: payload.patientId } ); // move to the next step
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /**
     * @desc Request backend to register `member` as the caller for the ongoing inbound call.
     */
    async registerInboundCaller( { actionName, url, payload, callerType } ) {
        log.debug( 'AuthFlowCallerIdentificationMemberWidget.registerInboundCaller()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationMemberService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status } = await this.authFlowCallerIdentificationMemberService.registerInboundCaller( {
            url: url,
            data: payload,
            callerType: callerType
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // update call data in the store
                this.props.updateCallState( {
                    callerId: payload.id,
                    callerType: getCallerTypeFromName( this.props.widget.value.callerType ),
                    callerInfo: {
                        id: null,
                        refId: payload.id,
                        patientId: payload.patientId,
                        type: getCallerTypeFromName( this.props.widget.value.callerType ),
                        firstName: null,
                        lastName: null,
                    },
                } );

                this.props.onNextActionClick( { name: actionName, memberId: payload.id, patientId: payload.patientId } ); // move to the next step
            } );
        } else {
            errorOverlay.open(); // show error
        }

    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.authFlowCallerIdentificationMemberService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <AuthFlowCallerIdentificationMemberView
                className={ this.props.className }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={ this.handleActionClick }
                callState={ this.props.callState }
                requestPayload= { this.state.requestPayload }
            />
        );
    }
}

// set display name
_AuthFlowCallerIdentificationMemberWidget.displayName = '_AuthFlowCallerIdentificationMemberWidget';

// set default props
_AuthFlowCallerIdentificationMemberWidget.defaultProps = {
    widget: {},
    onNextActionClick: noop,
};

// prop types
_AuthFlowCallerIdentificationMemberWidget.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const AuthFlowCallerIdentificationMemberWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowCallerIdentificationMemberWidget ) );
